// Client-side attachment text extraction (stage 0 of the matching pipeline).
// Attachment bytes are fetched from the provider, parsed to plain text in the
// browser, and the TEXT (never the binary) may be cached in IndexedDB. Nothing
// leaves the machine. Scanned/image PDFs yield no text and are marked
// unparseable (OCR is out of scope for v1).
//
// pdfjs and mammoth are lazy-loaded (same pattern as OaAnalysis.vue) so the
// initial bundle stays lean — parsing is rare and on-demand.

const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;   // skip individual files over ~15 MB
const MAX_BYTES_PER_MESSAGE = 30 * 1024 * 1024;  // cap total parsed bytes per message
const MAX_PDF_PAGES = 50;
const PARSEABLE_MIME = /pdf|msword|officedocument|text\//i;
const PARSEABLE_EXT = /\.(pdf|docx|txt|csv|eml)$/i;

function isParseable(att) {
  return PARSEABLE_MIME.test(att.mimeType || '') || PARSEABLE_EXT.test(att.name || '');
}

function base64ToArrayBuffer(b64) {
  // gmail returns base64url WITHOUT padding, Graph returns standard base64;
  // atob requires both the standard alphabet and length padding
  let normalized = (b64 || '').replace(/-/g, '+').replace(/_/g, '/');
  while (normalized.length % 4 !== 0) {
    normalized += '=';
  }
  const bin = atob(normalized);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes.buffer;
}

async function extractPdfText(arrayBuffer) {
  const pdfjsLib = await import('pdfjs-dist/build/pdf');
  const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
  // real-world PDFs often carry slightly malformed streams (e.g. bad flate
  // FCHECK); pdfjs recovers what it can and our try/catch handles hard
  // failures — keep the console to genuine errors
  pdfjsLib.GlobalVerbosityLevel = pdfjsLib.VerbosityLevel.ERRORS;

  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pages = Math.min(pdf.numPages, MAX_PDF_PAGES);
  let text = '';
  for (let i = 1; i <= pages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map((item) => item.str).join(' ') + '\n';
  }
  return text;
}

async function extractText(att, arrayBuffer) {
  const mime = att.mimeType || '';
  const name = att.name || '';
  if (/pdf/i.test(mime) || /\.pdf$/i.test(name)) {
    return await extractPdfText(arrayBuffer);
  }
  if (/officedocument\.wordprocessingml|msword/i.test(mime) || /\.docx$/i.test(name)) {
    const mammoth = await import('mammoth/mammoth.browser');
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value || '';
  }
  if (/text\//i.test(mime) || /\.(txt|csv|eml)$/i.test(name)) {
    return new TextDecoder().decode(arrayBuffer);
  }
  return '';
}

// Fetches and parses the parseable attachments of one message, with guards.
// Returns concatenated text ("filename\n text..."). Unparseable/oversized/
// failed attachments are skipped silently — the caller records them as parsed
// so they are never retried in a hot loop.
async function extractAttachmentsText(message, attachments, emailApi) {
  let text = '';
  let totalBytes = 0;
  for (const att of attachments) {
    if (!att || !att.attachmentId || !isParseable(att)) {
      continue;
    }
    if (att.size > MAX_ATTACHMENT_BYTES || totalBytes + att.size > MAX_BYTES_PER_MESSAGE) {
      continue;
    }
    totalBytes += att.size;
    try {
      const b64 = await emailApi.fetchAttachmentData(message.id, att.attachmentId);
      const arrayBuffer = base64ToArrayBuffer(b64);
      const attText = await extractText(att, arrayBuffer);
      if (attText) {
        text += `\n${att.name}\n${attText}`;
      }
    } catch (e) {
      console.warn(`attachment parse skipped (${att.name})`, e);
    }
  }
  return text;
}

export {
  isParseable,
  extractAttachmentsText,
  base64ToArrayBuffer,
};
